import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import useFullscreenImage from '../../../hooks/useFullscreenImage'
import { CloseButtonCause, CurrentSubView, ListStatus, Mode, RemoteButtonType, RetorikEvent, Routes } from '../../../models/enums'
import MicrophoneButton from '../Common/MicrophoneButton'
import RemoteButton from './RemoteButton'

interface MicrophoneAndSwitchProps {
  top?: number | string
}

const MicrophoneAndSwitch = ({ top }: MicrophoneAndSwitchProps): JSX.Element => {
  const fullScreenImage = useFullscreenImage()
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const isLandscape = useSelector((state: RootState) => state.viewReducer.isLandscape)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const disabledTextMode = useSelector((state: RootState) => state.retorikReducer.configuration.disableTextMode)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const dashboardOpened = useSelector((state: RootState) => state.utilsReducer.dashboardOpened)
  const retorikEvent = useSelector((state: RootState) => state.utilsReducer.retorikEvent)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const [showCloseButton, setShowCloseButton] = useState<CloseButtonCause>(CloseButtonCause.none)
  const [bottomForBorne, setBottomForBorne] = useState<string>()

  useEffect(() => {
    if (
      !isLandscape &&
      (currentSubView !== CurrentSubView.none || dashboardOpened || retorikEvent === RetorikEvent.DetailViewOpen || listStatus === ListStatus.opened || route === Routes.News || fullScreenImage)
    ) {
      /**
       * Closing order :
       * dashboard -> subview but map -> fullscreen image -> detailed view -> map -> list -> news
       */
      if (dashboardOpened) {
        setShowCloseButton(CloseButtonCause.dashboard)
      } else if (currentSubView !== CurrentSubView.none && currentSubView !== CurrentSubView.map) {
        switch (currentSubView) {
          case CurrentSubView.emergency:
            setShowCloseButton(CloseButtonCause.emergency)
            break
          case CurrentSubView.history:
            setShowCloseButton(CloseButtonCause.history)
            break
          case CurrentSubView.languages:
            setShowCloseButton(CloseButtonCause.languages)
            break
          case CurrentSubView.weather:
            setShowCloseButton(CloseButtonCause.weather)
            break
        }
      } else if (fullScreenImage) {
        setShowCloseButton(CloseButtonCause.fullscreenimage)
      } else if (retorikEvent === RetorikEvent.DetailViewOpen) {
        setShowCloseButton(CloseButtonCause.detailedpoi)
      } else if (currentSubView === CurrentSubView.map) {
        setShowCloseButton(CloseButtonCause.map)
      } else if (listStatus === ListStatus.opened) {
        setShowCloseButton(CloseButtonCause.list)
      } else if (route === Routes.News) {
        setShowCloseButton(CloseButtonCause.news)
      }
    } else {
      setShowCloseButton(CloseButtonCause.none)
    }
  }, [isLandscape, currentSubView, dashboardOpened, retorikEvent, listStatus, route, fullScreenImage])

  useEffect(() => {
    if (!top || isLandscape) {
      setBottomForBorne(undefined)
    } else {
      setBottomForBorne(typeof top === 'number' ? `calc(100% - ${top}px + 0.5rem)` : `calc(100% - ${top} + 0.5rem)`)
    }
  }, [isLandscape, top])

  return (
    <div
      id='retorik-microphone-container'
      className={`large-vertical:rf-absolute rf-w-fit ${
        showCloseButton !== CloseButtonCause.none ? 'rf-right-9' : 'rf-right-4'
      } landscape-retorik:-rf-mt-4 landscape-retorik:rf-pl-[calc(50%-2.5rem)] rf-flex rf-flex-row large-vertical:rf-flex-col rf-justify-center rf-items-center rf-gap-6 large-vertical:rf-gap-4`}
      style={{
        bottom: bottomForBorne
      }}
    >
      {/* Microphone button hidden if showCloseButton is true */}
      {mode === Mode.vocal && <MicrophoneButton hidden={showCloseButton !== CloseButtonCause.none} />}
      {(showCloseButton !== CloseButtonCause.none || mode === Mode.vocal) &&
        (showCloseButton === CloseButtonCause.none ? (
          /* If the button has no closing cause, it should be the one to switch to text mode. If disabledTextMode is true, then we show nothing */
          disabledTextMode ? (
            <React.Fragment />
          ) : (
            <RemoteButton type={RemoteButtonType.text} backgroundColor='#FFF' cause={showCloseButton} />
          )
        ) : (
          <RemoteButton type={RemoteButtonType.close} backgroundColor='#FFF' cause={showCloseButton} />
        ))}
    </div>
  )
}

export default MicrophoneAndSwitch
